/**
 * The one place this page renders a refusal.
 *
 * A refusal is always shown, never swallowed and never retried on the user's
 * behalf — a step-up refusal in particular is surfaced as-is, because this page
 * has no business completing a step-up for anyone. The visible line is the
 * mapped sentence from `copy.ts` (or the unmapped code, bounded, for a code
 * this console has not learned); the raw code rides in a `data-refusal-code`
 * attribute instead of being printed twice.
 */

import type { ApiErrorInfo } from "@/lib/api";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { cn } from "@/lib/utils";
import { boundedCode, refusalMessage } from "./copy";

export function RefusalNotice({
  error,
  title,
  /**
   * `console` marks a refusal this page raised before sending anything (a
   * money value the asset cannot carry). Saying so is the honest version:
   * otherwise a reader would think the service had answered.
   */
  source = "service",
  className,
}: {
  error: ApiErrorInfo;
  title: string;
  source?: "service" | "console";
  className?: string;
}) {
  const code = boundedCode(error.reasonCode);
  return (
    <div
      role="alert"
      data-testid="refusal-notice"
      data-refusal-code={code}
      data-refusal-source={source}
      className={cn(
        "rounded-md border border-danger-border bg-danger-soft px-3.5 py-3 text-sm",
        className,
      )}
    >
      <div className="font-semibold text-danger">{title}</div>
      <p className="mt-1 break-words leading-relaxed text-danger">
        {refusalMessage(code)}
      </p>
      {source === "console" && (
        <p className="mt-1 text-xs text-danger">Nothing was sent to the service.</p>
      )}
      <SupportDetails reference={error.supportReference} capability={error.capability} />
    </div>
  );
}
